/**
 * stepLabel.ts: the words a tool step wears when nothing narrated it.
 *
 * Several surfaces label a step from its block alone: the story's receipt, the
 * trail's command strip, the home card's running tool. They read the same
 * helpers here, so one step never says "Ran a command" in one place and "List
 * files in the repo" in another (founder 09-30: a coding-engine task read "Ran
 * a command" fifteen times).
 */
import { tensed } from "./verbs";

const upperFirst = (s: string): string =>
  s.charAt(0).toUpperCase() + s.slice(1);

/**
 * A command step. The coding engine's Bash tool carries a `description` on
 * every call, a short plain sentence ("List files in the repo"); the step says
 * it in its own tense: "Listed files in the repo" once settled, "Listing files
 * in the repo" while it runs. A description whose first word is not a verb the
 * table knows is said as written. No description: the plain command sentence.
 */
export function bashStepLabel(
  input: Record<string, unknown> | null | undefined,
  done: boolean
): string {
  const raw = typeof input?.description === "string" ? input.description : "";
  const said = (raw.split("\n")[0] ?? "").trim().replace(/[.]+$/, "").trim();
  if (!said) return done ? "Ran a command" : "Running a command";
  return upperFirst(tensed(said, done ? "past" : "progressive") ?? said);
}

// The coding engine's housekeeping tools: [settled, running].
const PLAIN_STEPS: ReadonlyMap<string, readonly [string, string]> = new Map([
  ["ToolSearch", ["Looked up a tool", "Looking up a tool"]],
  ["LSP", ["Checked the code", "Checking the code"]],
  ["WaitForMcpServers", ["Waited for tools", "Waiting for tools"]],
]);

/** A housekeeping tool's step in plain words; null for any other tool. */
export function plainStepLabel(toolName: string, done: boolean): string | null {
  const said = PLAIN_STEPS.get(toolName);
  return said ? said[done ? 0 : 1] : null;
}
